import { useEffect, useRef, useState } from 'react';

import { GridFilter, GridOption } from './model';

type Props = {
  filter: GridFilter;
  value: string;
  disabled?: boolean;
  labels?: Record<string, string>;
  onChange: (value: string, options?: GridOption[]) => void;
};

export function LookupFilter({
  filter,
  value,
  disabled,
  labels = {},
  onChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [remote, setRemote] = useState<GridOption[]>([]);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const cache = useRef<Record<string, string>>({});
  const lookup = filter.lookup;
  const selected = value.split(',').filter(Boolean);
  const multiple = filter.filterType === 'ids';
  const options = lookup?.search
    ? remote
    : (filter.options || []).filter((o) =>
        o.label.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())
      );
  [...(filter.options || []), ...remote].forEach((o) => {
    cache.current[o.value] = o.label;
  });
  useEffect(() => {
    if (!open || !lookup?.search || disabled) {
      return;
    }
    let live = true;
    setLoading(true);
    setError('');
    const timer = setTimeout(() => {
      lookup
        .search(search.trim(), offset)
        .then((result) => {
          if (!live) {
            return;
          }
          setRemote((previous) =>
            Array.from(
              new Map(
                [...(offset ? previous : []), ...result.options].map((o) => [
                  o.value,
                  o,
                ])
              ).values()
            )
          );
          setMore(result.hasMore);
          setLoading(false);
        })
        .catch(() => {
          if (live) {
            setLoading(false);
            setError('Не удалось загрузить варианты');
          }
        });
    }, 250);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [open, lookup, search, offset, retry, disabled]);
  const title = (id: string) =>
    cache.current[id] ||
    labels[id] ||
    (id === 'null' ? 'Не задан' : `Значение № ${id}`);
  const update = (ids: string[]) =>
    onChange(
      ids.join(','),
      ids.map((id) => ({ value: id, label: title(id) }))
    );
  return (
    <div className="grid-lookup">
      <button
        type="button"
        disabled={disabled}
        aria-label={`Фильтр: ${filter.label}`}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {selected.length
          ? selected.map(title).join(', ')
          : 'Выбрать по названию…'}
      </button>
      {open && (
        <div className="grid-lookup-panel">
          <input
            aria-label={`Поиск: ${filter.label}`}
            placeholder="Поиск по названию…"
            disabled={disabled}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOffset(0);
              setRemote([]);
            }}
          />
          {selected.length > 0 && (
            <div>
              {selected.map((id) => (
                <button
                  key={id}
                  type="button"
                  disabled={disabled}
                  aria-label={`Убрать ${title(id)}`}
                  onClick={() => update(selected.filter((v) => v !== id))}
                >
                  {title(id)} ×
                </button>
              ))}
              <button
                type="button"
                disabled={disabled}
                onClick={() => update([])}
              >
                Очистить выбор
              </button>
            </div>
          )}
          {!multiple && <small>Можно выбрать одно значение</small>}
          {lookup?.unavailable && (
            <span role="status">Справочник пока недоступен</span>
          )}
          {loading && <span role="status">Поиск…</span>}
          {error && (
            <div role="alert">
              {error}{' '}
              <button
                type="button"
                disabled={disabled}
                onClick={() => setRetry((n) => n + 1)}
              >
                Повторить поиск
              </button>
            </div>
          )}
          <div className="grid-lookup-options">
            {filter.filterType === 'nullable-integer' && (
              <button
                type="button"
                disabled={disabled}
                onClick={() => update(['null'])}
              >
                Не задан
              </button>
            )}
            {options.map((option) => (
              <label key={option.value}>
                <input
                  type="checkbox"
                  checked={selected.includes(option.value)}
                  disabled={disabled || loading}
                  onChange={() =>
                    update(
                      selected.includes(option.value)
                        ? selected.filter((id) => id !== option.value)
                        : multiple
                        ? [...selected, option.value]
                        : [option.value]
                    )
                  }
                />
                {option.label}
              </label>
            ))}
          </div>
          {!loading && !error && !options.length && !lookup?.unavailable && (
            <span>Ничего не найдено</span>
          )}
          {more && (
            <button
              type="button"
              disabled={disabled || loading}
              onClick={() => setOffset((n) => n + 30)}
            >
              Показать ещё
            </button>
          )}
          <button type="button" onClick={() => setOpen(false)}>
            Закрыть
          </button>
        </div>
      )}
    </div>
  );
}
